import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { IssueIcon } from "@/lib/thematic";
import { cn } from "@/lib/utils/cn";

import { useIcons } from "../api";
import { FONT_AWESOME_GALLERY } from "../constants";

/**
 * Choose an issue icon among the free Font Awesome icons, as the Django form's
 * picker does: the server reads the list from the installed package, and the
 * search narrows it as it is typed, over each icon's class, label and Font
 * Awesome's own search terms.
 */
export function IconPicker({
  current,
  onPick,
  onClose,
}: {
  current: string;
  onPick: (icon: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const icons = useIcons();
  const [query, setQuery] = useState("");

  const indexed = useMemo(
    () =>
      (icons.data?.icons ?? []).map((entry) => ({
        ...entry,
        haystack: [entry.icon, entry.label, ...entry.terms].join(" ").toLowerCase(),
      })),
    [icons.data],
  );
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = indexed.filter((entry) => words.every((word) => entry.haystack.includes(word)));
  const unavailable = icons.isError || (icons.isSuccess && indexed.length === 0);

  return (
    <Modal title={t("catalogue.icon.choose")} onClose={onClose} className="max-w-3xl">
      {icons.isPending ? (
        <p className="text-sm text-muted-foreground">{t("catalogue.icon.loading")}</p>
      ) : unavailable ? (
        <p role="alert" className="rounded-md border border-border bg-muted p-3 text-sm">
          {t("catalogue.icon.unavailable")}{" "}
          <a
            href={FONT_AWESOME_GALLERY}
            target="_blank"
            rel="noreferrer"
            className="text-primary underline-offset-4 hover:underline"
          >
            {t("catalogue.icon.gallery")}
          </a>
        </p>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="icon-picker-search" className="block">
              {t("catalogue.icon.search")}
            </Label>
            <Input
              id="icon-picker-search"
              type="search"
              autoComplete="off"
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              aria-describedby="icon-picker-search-help"
            />
            <p id="icon-picker-search-help" className="text-xs text-muted-foreground">
              <Trans
                i18nKey="catalogue.icon.searchHint"
                components={{
                  gallery: (
                    <a
                      href={FONT_AWESOME_GALLERY}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary underline-offset-4 hover:underline"
                    />
                  ),
                }}
              />
            </p>
          </div>
          {shown.length === 0 ? (
            <p role="status" className="text-sm text-muted-foreground">
              {t("catalogue.icon.empty")}
            </p>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(6rem,1fr))] gap-2">
              {shown.map((entry) => {
                const selected = entry.icon === current.trim();
                return (
                  <button
                    key={entry.icon}
                    type="button"
                    title={entry.icon}
                    aria-pressed={selected}
                    onClick={() => onPick(entry.icon)}
                    className={cn(
                      "flex flex-col items-center gap-1.5 rounded-md border border-input bg-card px-1 py-2.5 text-center text-xs leading-tight break-words hover:border-primary focus-visible:border-primary focus-visible:outline-none",
                      selected && "border-primary ring-1 ring-primary",
                    )}
                  >
                    <IssueIcon icon={entry.icon} className="size-5" />
                    {entry.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
